// colorsApi.ts
import { createApi } from '@reduxjs/toolkit/query/react';
import { waServicesQuery } from './baseQueries';
import { defaultChartColors } from '@/constants/defaultColors';
import type { RecordResponse } from '@/models/ServiceTypes';

type AdvisorGroupColorRecord = {
  AdvisorGroupColorId?: number;
  AdvisorGroupId?: number;
  HexColorCode?: string | null;
  HexColor?: string | null;
  SortOrder?: number | null;
};

type AdvisorGroupColorsResponse =
  | RecordResponse<AdvisorGroupColorRecord>
  | AdvisorGroupColorRecord[];

function normalizeCssHexColor(value: string | null | undefined): string | null {
  const trimmed = value?.trim() ?? '';
  if (trimmed.length === 0) {
    return null;
  }
  // Accept common hex formats even when '#' is missing.
  if (/^[0-9a-fA-F]{6}([0-9a-fA-F]{2})?$/.test(trimmed)) {
    return `#${trimmed}`;
  }
  if (/^#[0-9a-fA-F]{6}([0-9a-fA-F]{2})?$/.test(trimmed)) {
    return trimmed;
  }
  return null;
}

function normalizeAdvisorGroupColorsResponse(
  response: AdvisorGroupColorsResponse,
): AdvisorGroupColorRecord[] {
  if (Array.isArray(response)) {
    return response;
  }
  return Array.isArray(response?.Records) ? response.Records : [];
}

export const colorsApi = createApi({
  reducerPath: 'colorsApi',
  baseQuery: waServicesQuery,
  endpoints: (builder) => ({
    getUserColors: builder.query<string[], void>({
      query: () => '/api/user/colors',
      transformResponse: (response: string[]) => {
        if (!response || !response.length) {
          return defaultChartColors;
        }
        return response;
      },
    }),
    getAdvisorGroupColors: builder.query<string[], { advisorGroupId: number }>({
      query: ({ advisorGroupId }) =>
        `/api/firm/advisorGroups/${encodeURIComponent(String(advisorGroupId))}/colors`,
      transformResponse: (response: AdvisorGroupColorsResponse) => {
        const records = normalizeAdvisorGroupColorsResponse(response);

        const palette = records
          .slice()
          .sort((a, b) => {
            const ao =
              typeof a.SortOrder === 'number'
                ? a.SortOrder
                : Number.POSITIVE_INFINITY;
            const bo =
              typeof b.SortOrder === 'number'
                ? b.SortOrder
                : Number.POSITIVE_INFINITY;
            if (ao !== bo) return ao - bo;
            const aid =
              typeof a.AdvisorGroupColorId === 'number'
                ? a.AdvisorGroupColorId
                : 0;
            const bid =
              typeof b.AdvisorGroupColorId === 'number'
                ? b.AdvisorGroupColorId
                : 0;
            return aid - bid;
          })
          .map((r) => normalizeCssHexColor(r.HexColorCode ?? r.HexColor))
          .filter((v): v is string => typeof v === 'string' && v.length > 0);

        return palette.length > 0 ? palette : defaultChartColors;
      },
    }),
  }),
});

export const { useGetUserColorsQuery, useGetAdvisorGroupColorsQuery } =
  colorsApi;
